import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  alpha,
  TextField,
  ClickAwayListener,
} from "@mui/material";
import {
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Language as SiteIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { SiteListItem } from "../../types";

interface SiteSelectorProps {
  sites: SiteListItem[];
  currentSite: SiteListItem | null;
  onSelectSite: (site: SiteListItem) => void;
  onCreateSite: () => void;
  onDeleteSite: (site: SiteListItem) => void;
  onRenameSite?: (site: SiteListItem, newName: string) => void;
}

export const SiteSelector: React.FC<SiteSelectorProps> = ({
  sites,
  currentSite,
  onSelectSite,
  onCreateSite,
  onDeleteSite,
  onRenameSite,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (editingSiteId) {
      setTimeout(() => editInputRef.current?.select(), 0);
    }
  }, [editingSiteId]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setEditingSiteId(null);
  };

  const handleSelectSite = (site: SiteListItem) => {
    if (editingSiteId) return;
    onSelectSite(site);
    handleClose();
  };

  const handleDeleteClick = (e: React.MouseEvent, site: SiteListItem) => {
    e.stopPropagation();
    onDeleteSite(site);
    handleClose();
  };

  const startEditing = (e: React.MouseEvent, site: SiteListItem) => {
    e.stopPropagation();
    setEditingSiteId(site.id);
    setEditName(site.name);
  };

  const confirmEditing = () => {
    if (!editingSiteId) return;
    const trimmed = editName.trim();
    const site = sites.find((s) => s.id === editingSiteId);
    if (trimmed && site && trimmed !== site.name) {
      onRenameSite?.(site, trimmed);
    }
    setEditingSiteId(null);
  };

  const cancelEditing = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingSiteId(null);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: "grey.50",
          border: 1,
          borderColor: "grey.300",
          color: "text.primary",
          textTransform: "none",
          justifyContent: "flex-start",
          minWidth: 200,
          "&:hover": {
            bgcolor: "grey.100",
            borderColor: "grey.400",
          },
        }}
      >
        {currentSite ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: `linear-gradient(135deg, ${currentSite.theme.primaryColor} 0%, ${currentSite.theme.secondaryColor} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SiteIcon sx={{ fontSize: 18, color: "white" }} />
            </Box>
            <Box sx={{ flex: 1, textAlign: "left" }}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {currentSite.name}
              </Typography>
              <Typography variant="caption" color="grey.500" noWrap>
                {currentSite.pageCount || 0} pages
              </Typography>
            </Box>
            <ArrowDownIcon sx={{ color: "grey.500" }} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SiteIcon sx={{ color: "grey.500" }} />
            <Typography color="grey.400">Select Site</Typography>
            <ArrowDownIcon sx={{ color: "grey.500", ml: "auto" }} />
          </Box>
        )}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            bgcolor: "white",
            border: 1,
            borderColor: "grey.200",
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          },
        }}
      >
        {/* Sites List */}
        {sites.length > 0 ? (
          sites.map((site) => {
            const isEditing = editingSiteId === site.id;

            if (isEditing) {
              return (
                <MenuItem key={site.id} disableRipple sx={{ py: 1, px: 2, "&:hover": { bgcolor: "transparent" } }}>
                  <ClickAwayListener onClickAway={confirmEditing}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                      <TextField
                        inputRef={editInputRef}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") confirmEditing();
                          if (e.key === "Escape") cancelEditing();
                        }}
                        size="small"
                        autoFocus
                        fullWidth
                        sx={{ "& .MuiInputBase-input": { fontSize: "0.9rem", py: 0.75 } }}
                      />
                      <IconButton size="small" onClick={confirmEditing} sx={{ color: "success.main" }}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={cancelEditing} sx={{ color: "grey.500" }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ClickAwayListener>
                </MenuItem>
              );
            }

            return (
              <MenuItem
                key={site.id}
                onClick={() => handleSelectSite(site)}
                selected={currentSite?.id === site.id}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&.Mui-selected": { bgcolor: alpha("#3b82f6", 0.1) },
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      background: `linear-gradient(135deg, ${site.theme.primaryColor} 0%, ${site.theme.secondaryColor} 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SiteIcon sx={{ fontSize: 18, color: "white" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography color="text.primary" fontWeight={500} fontSize="0.9rem">
                      {site.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        /{site.slug}
                      </Typography>
                      <Chip
                        label={`${site.pageCount || 0} pages`}
                        size="small"
                        sx={{ height: 18, fontSize: "0.65rem", bgcolor: "grey.200", color: "grey.600" }}
                      />
                    </Box>
                  </Box>
                  {onRenameSite && (
                    <IconButton
                      size="small"
                      onClick={(e) => startEditing(e, site)}
                      sx={{
                        color: "grey.500",
                        opacity: 0,
                        transition: "opacity 0.2s",
                        ".MuiMenuItem-root:hover &": { opacity: 1 },
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(e, site)}
                    sx={{
                      color: "grey.500",
                      opacity: 0,
                      transition: "opacity 0.2s",
                      ".MuiMenuItem-root:hover &": { opacity: 1 },
                      "&:hover": { color: "error.main" },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </MenuItem>
            );
          })
        ) : (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <SiteIcon sx={{ fontSize: 40, color: "grey.600", mb: 1 }} />
            <Typography color="grey.500" variant="body2">
              ยังไม่มี Site
            </Typography>
          </Box>
        )}

        <Divider sx={{ borderColor: "grey.200", my: 1 }} />

        {/* Create New Site */}
        <MenuItem
          onClick={() => {
            onCreateSite();
            handleClose();
          }}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": { bgcolor: alpha("#10b981", 0.1) },
          }}
        >
          <ListItemIcon>
            <AddIcon sx={{ color: "success.main" }} />
          </ListItemIcon>
          <ListItemText primary="สร้าง Site ใหม่" primaryTypographyProps={{ color: "success.main", fontWeight: 500 }} />
        </MenuItem>
      </Menu>
    </>
  );
};
